import { RefObject, useEffect, useRef } from 'react';
import { useChatSearchContext } from './ChatSearch.context';

/**
 * Ctrl/⌘+F opens the in-chat search — but only while the chat panel is where
 * the user is. Anywhere else the browser's own find keeps working.
 *
 * "Where the user is" is the last pointer-down or focus, not `:focus-within`:
 * clicking into the thread to read it focuses nothing (the bubbles are not
 * focusable), and that is exactly when someone reaches for Ctrl+F.
 *
 * Each chat panel runs its own, so the Messenger page and a chat modal never
 * both answer the same keystroke. A no-op outside a ChatSearchProvider.
 *
 * @param panelRef - The chat panel's root (`.Chat`).
 */
export default function useChatSearchShortcut(
  panelRef: RefObject<HTMLElement>,
): void {
  const chatSearch = useChatSearchContext();
  const isEnabled = Boolean(chatSearch);
  const panelIsActiveRef = useRef(false);
  const openRef = useRef(chatSearch?.open);
  openRef.current = chatSearch?.open;

  useEffect(() => {
    if (!isEnabled) return;

    const isInsidePanel = (target: EventTarget | null) =>
      target instanceof Node && Boolean(panelRef.current?.contains(target));

    const track = (event: Event) => {
      panelIsActiveRef.current = isInsidePanel(event.target);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey)
        return;
      if (event.key.toLowerCase() !== 'f') return;
      if (!panelIsActiveRef.current && !isInsidePanel(event.target)) return;

      event.preventDefault();
      // No `animate`: from the keyboard the bar opens at once, never animated
      // (DS guidelines/motion.md, "Keyboard work is instant").
      openRef.current?.({ trigger: 'shortcut' });
    };

    document.addEventListener('pointerdown', track, true);
    document.addEventListener('focusin', track, true);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', track, true);
      document.removeEventListener('focusin', track, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isEnabled, panelRef]);
}
